import { defineStore } from 'pinia'

export type CoverId = 'sheet' | 'docs' | 'mail' | 'kanban'
export type MobileCoverId = 'memo' | 'calc' | 'weather' | 'todo' // 폰 위장(메모장·계산기 Free / 날씨·할일 Pro)
export type MotionOpt = 'auto' | 'on' | 'off'
export type AwayOpt = 'cover' | 'veil' | 'none'
export type ThemeOpt = 'auto' | 'light' | 'dark'

interface WebOpts {
  cover: CoverId
  coverMobile: MobileCoverId // 폰 셸에서 쓰는 위장(웹과 별도 — 폰은 메모장·계산기 등)
  start: boolean
  away: AwayOpt
  idle: number // seconds, 0 = off
  pin: string
  motion: MotionOpt
  theme: ThemeOpt
  autoLockMin: number // 금고 자동 잠금 분, 0 = 안 함
}

const DEFAULT_OPTS: WebOpts = { cover: 'sheet', coverMobile: 'memo', start: true, away: 'cover', idle: 30, pin: '1004', motion: 'auto', theme: 'auto', autoLockMin: 5 }
const COVERS: CoverId[] = ['sheet', 'docs', 'mail', 'kanban']
const MOBILE_COVERS: MobileCoverId[] = ['memo', 'calc', 'weather', 'todo']

function loadOpts(): WebOpts {
  const o = { ...DEFAULT_OPTS }
  try { Object.assign(o, JSON.parse(localStorage.getItem('webOpts') || '{}')) } catch { /* ignore */ }
  if (!COVERS.includes(o.cover)) o.cover = 'sheet'
  if (!MOBILE_COVERS.includes(o.coverMobile)) o.coverMobile = 'memo'
  return o
}
function loadStealth(): boolean {
  try { return localStorage.getItem('stealth') !== '0' } catch { return true } // 업무 모드 기본 켜짐
}
function loadOnboarded(): boolean {
  try { return localStorage.getItem('onboarded') === '1' } catch { return false }
}

export const useUiStore = defineStore('ui', {
  state: () => ({
    opts: loadOpts(),
    stealth: loadStealth(), // 업무 모드(웹 기본값: 켜짐)
    appMode: false,         // 폰/네이티브 셸: 화려한 '앱' 표현(업무 모드 회색·용어 미적용). 저장하지 않음 — 웹 stealth 기본값 보존
    overlayOpen: false,     // 폰 모달(성향 시트·관리)이 열려 있는지 — 열리면 꾹-녹음 FAB를 숨긴다
    coverOn: false,         // 위장 화면 표시 중
    veil: false,            // 자리 비움 흐리게
    awayHoldUntil: 0,       // 이 시각까지 자리비움/자동잠금을 보류(파일 선택·마이크 권한 등 의도된 blur)
    onboarded: loadOnboarded(),
  }),
  getters: {
    // reduced-motion 또는 설정에 따라 "실제로 움직일지"
    motionActive(state): boolean {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduced) return false
      if (state.opts.motion === 'on') return true
      if (state.opts.motion === 'off') return false
      if (state.appMode) return true // 앱(폰)은 화려하게 — auto면 항상 켠다
      return !state.stealth // auto: 업무 모드에서만 끈다
    },
  },
  actions: {
    saveOpts() { try { localStorage.setItem('webOpts', JSON.stringify(this.opts)) } catch { /* ignore */ } },
    setStealth(on: boolean) {
      this.stealth = on
      if (on) this.veil = false
      try { localStorage.setItem('stealth', on ? '1' : '0') } catch { /* ignore */ }
    },
    toggleStealth() { this.setStealth(!this.stealth) },
    setAppMode(on: boolean) { this.appMode = on },
    setMotion(m: MotionOpt) { this.opts.motion = m; this.saveOpts() },
    applyTheme() {
      const el = document.documentElement
      if (this.opts.theme === 'auto') delete el.dataset.theme
      else el.dataset.theme = this.opts.theme
    },
    setOnboarded() { this.onboarded = true; try { localStorage.setItem('onboarded', '1') } catch { /* ignore */ } },
    // 파일 선택 대화상자·마이크 권한 등 의도된 blur 동안 자리비움/자동잠금을 보류한다
    holdAway(ms = 180000) { this.awayHoldUntil = Date.now() + ms },
    showCover() { this.coverOn = true },
    hideCover() { this.coverOn = false },
    setVeil(v: boolean) { this.veil = v },
  },
})
